'use client';

import {
  forwardRef,
  ForwardRefRenderFunction,
  OptionHTMLAttributes,
  SelectHTMLAttributes,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import {
  isElementWithProps,
  getChildrenAsArrayOfElements,
} from '../../../utils';
import { RadixSelect, RadixSelectItem } from '../RadixSelect';

type Option = OptionHTMLAttributes<HTMLOptionElement>;

type Value = Option['value'];

type BaseProps = {
  invalid?: string;
  placeholder?: string;
  options?: Option[];
  normalizeHeight?: boolean;
  truncatePreview?: boolean;
};

type CompatibleProps = Pick<
  SelectHTMLAttributes<HTMLSelectElement>,
  | 'children'
  | 'value'
  | 'defaultValue'
  | 'dir'
  | 'name'
  | 'autoComplete'
  | 'disabled'
  | 'required'
  | 'onChange'
  | 'className'
>;

export type SelectProps = BaseProps &
  Omit<CompatibleProps, keyof BaseProps | 'type'>;

const SelectRenderFunction: ForwardRefRenderFunction<
  HTMLSelectElement | null,
  SelectProps
> = (
  {
    invalid,
    children,
    options = [],
    value,
    defaultValue,
    onChange,
    normalizeHeight,
    truncatePreview,
    ...rest
  },
  forwardedRef,
) => {
  const uniqueId = useId();
  const ref = useRef<HTMLSelectElement | null>(null);
  const radixRef = useRef<HTMLButtonElement | null>(null);

  useImperativeHandle<HTMLSelectElement | null, HTMLSelectElement | null>(
    forwardedRef,
    () => ref.current,
  );

  const combinedOptions = useMemo(() => {
    const optionsFromChildren = getChildrenAsArrayOfElements(children)
      .filter(isElementWithProps)
      .map((node): Option => node.props);

    return [...options, ...optionsFromChildren];
  }, [children, options]);

  const handleRadixChange = (radixValue: string) => {
    const selectEl = ref.current;

    if (!selectEl) {
      return;
    }

    const value = getValueFromIndex(combinedOptions, radixValue);
    selectEl.selectedIndex = parseInt(radixValue ?? '0', 10);

    if (value) {
      selectEl.value = value.toString();
    }

    selectEl.dispatchEvent(
      new Event('change', {
        bubbles: true,
      }),
    );
  };

  const providedValue = useMemo(() => {
    const idx = getIndexFromValue(combinedOptions, value);
    return typeof idx === 'undefined' ? undefined : idx.toString();
  }, [combinedOptions, value]);

  const providedDefaultValue = useMemo(() => {
    const idx = getIndexFromValue(combinedOptions, defaultValue);
    return typeof idx === 'undefined' ? undefined : idx.toString();
  }, [combinedOptions, defaultValue]);

  useEffect(() => {
    if (!radixRef.current) {
      return;
    }

    if (invalid) {
      radixRef.current.dataset.invalid = 'true';
    } else {
      delete radixRef.current.dataset.invalid;
    }
  }, [radixRef, invalid]);

  return (
    <div className="relative">
      <select
        name={rest.name}
        onChange={onChange}
        ref={ref}
        className="absolute hidden"
        defaultValue={defaultValue}
        value={value}
      >
        {combinedOptions.map((option, idx) => (
          <option key={`${option.value}${idx}`} {...option} />
        ))}
      </select>
      <RadixSelect
        {...rest}
        name={`${uniqueId}.${rest.name}`}
        defaultValue={providedDefaultValue}
        value={providedValue}
        dir={rest.dir === 'rtl' ? 'rtl' : 'ltr'}
        onValueChange={handleRadixChange}
        contentClassName={normalizeHeight ? 'max-h-[300px]' : undefined}
        truncatePreview={truncatePreview}
        ref={radixRef}
      >
        {combinedOptions.map((option, idx) => (
          <RadixSelectItem
            key={`${option.value}${idx}`}
            value={(
              getIndexFromValue(combinedOptions, option.value) ?? ''
            ).toString()}
            disabled={option.disabled}
          >
            {option.children}
          </RadixSelectItem>
        ))}
      </RadixSelect>
    </div>
  );
};

const getIndexFromValue = (
  options: Option[],
  value: Value,
): number | undefined => {
  if (typeof value === 'undefined') {
    return undefined;
  }

  return options.findIndex(option => option.value === value);
};

const getValueFromIndex = (options: Option[], idx: string | number): Value => {
  idx = typeof idx === 'string' ? parseInt(idx, 10) : idx;

  return options[idx]?.value;
};

/**
 * A native `<select>` proxy which provides access to the RadixSelect component.
 */
export const Select = forwardRef<HTMLSelectElement | null, SelectProps>(
  SelectRenderFunction,
);
